import { Controller } from "@hotwired/stimulus"

interface Category {
  id: string
  name: string
  amount_cents?: number | null
  ai_predicted?: boolean
  ai_confidence?: number | null
}

export default class extends Controller {
  static targets = [
    "panel",
    "backdrop",
    "transactionName",
    "transactionAmount",
    "currentCategories",
    "noCategories",
    "remainingAmount",
    "searchInput",
    "dropdown",
    "dropdownList",
    "errorMessage",
    "badges",
    "internalToggle",
    "incomeToggle",
    "categorySection",
    "aiSuggestion",
    "aiCategoryName",
    "aiConfidence",
  ]

  static values = {
    categoriesUrl: String,
  }

  declare readonly panelTarget: HTMLElement
  declare readonly backdropTarget: HTMLElement
  declare readonly transactionNameTarget: HTMLElement
  declare readonly transactionAmountTarget: HTMLElement
  declare readonly currentCategoriesTarget: HTMLElement
  declare readonly noCategoriesTarget: HTMLElement
  declare readonly remainingAmountTarget: HTMLElement
  declare readonly searchInputTarget: HTMLInputElement
  declare readonly dropdownTarget: HTMLElement
  declare readonly dropdownListTarget: HTMLElement
  declare readonly errorMessageTarget: HTMLElement
  declare readonly badgesTargets: HTMLElement[]
  declare readonly internalToggleTarget: HTMLElement
  declare readonly incomeToggleTarget: HTMLElement
  declare readonly categorySectionTarget: HTMLElement
  declare readonly hasCategorySectionTarget: boolean
  declare readonly categoriesUrlValue: string
  declare readonly aiSuggestionTarget: HTMLElement
  declare readonly aiCategoryNameTarget: HTMLElement
  declare readonly aiConfidenceTarget: HTMLElement

  private currentTransactionId: string | null = null
  private currentTransactionAmount: number = 0
  private currentUpdateUrl: string | null = null
  private currentCategories: Category[] = []
  private currentInternal: boolean = false
  private currentIncome: boolean = false
  private allCategories: Category[] = []
  private boundCloseDropdown: (event: Event) => void = () => {}
  private currentAiPrediction: { categoryId: string; categoryName: string; confidence: number } | null = null

  connect() {
    this.currentTransactionId = null
    this.currentTransactionAmount = 0
    this.currentUpdateUrl = null
    this.currentCategories = []
    this.currentInternal = false
    this.currentIncome = false
    this.allCategories = this.loadAllCategories()

    // Close dropdown when clicking outside
    this.boundCloseDropdown = this.closeDropdownOnClickOutside.bind(this)
    document.addEventListener("click", this.boundCloseDropdown)
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseDropdown)
  }

  loadAllCategories(): Category[] {
    // Get categories from the hidden data element
    const dataEl = document.getElementById("category-data")
    if (!dataEl) return []

    const spans = dataEl.querySelectorAll("[data-category-id]")
    const categories: Category[] = []
    spans.forEach((span) => {
      const el = span as HTMLElement
      categories.push({
        id: el.dataset.categoryId!,
        name: el.dataset.categoryName!,
      })
    })
    return categories
  }

  openPanel(event: Event) {
    const button = event.currentTarget as HTMLElement
    const transactionId = button.dataset.transactionId!
    const row = this.element.querySelector(`[data-transaction-id="${transactionId}"]`) as HTMLElement

    this.currentTransactionId = transactionId
    this.currentTransactionAmount = parseInt(row.dataset.transactionAmount!)
    this.currentUpdateUrl = row.dataset.transactionUpdateUrl!

    // Set transaction info
    this.transactionNameTarget.textContent = row.dataset.transactionName!
    this.transactionAmountTarget.textContent = `$${(this.currentTransactionAmount / 100).toFixed(2)}`

    // Set system category states
    this.currentInternal = row.dataset.transactionInternal === "true"
    this.currentIncome = row.dataset.transactionIncome === "true"
    this.updateSystemToggleUI()

    // Check for AI prediction needing review
    if (row.dataset.transactionAiNeedsReview === "true") {
      this.currentAiPrediction = {
        categoryId: row.dataset.transactionAiCategoryId!,
        categoryName: row.dataset.transactionAiCategoryName!,
        confidence: parseFloat(row.dataset.transactionAiConfidence!),
      }
      this.aiCategoryNameTarget.textContent = this.currentAiPrediction.categoryName
      this.aiConfidenceTarget.textContent = `(${Math.round(this.currentAiPrediction.confidence * 100)}%)`
      this.aiSuggestionTarget.classList.remove("hidden")
    } else {
      this.currentAiPrediction = null
      this.aiSuggestionTarget.classList.add("hidden")
    }

    // Load current categories
    this.loadCurrentCategories()

    // Show panel
    this.backdropTarget.classList.remove("hidden")
    requestAnimationFrame(() => {
      this.panelTarget.style.transform = "translateX(0)"
    })

    document.body.style.overflow = "hidden"
  }

  closePanel() {
    this.panelTarget.style.transform = "translateX(100%)"
    this.backdropTarget.classList.add("hidden")
    document.body.style.overflow = ""

    this.currentTransactionId = null
    this.currentAiPrediction = null
    this.aiSuggestionTarget.classList.add("hidden")
    this.searchInputTarget.value = ""
    this.hideDropdown()
    this.clearError()
  }

  loadCurrentCategories() {
    const badgesContainer = this.badgesTargets.find(
      (b) => b.dataset.transactionId === this.currentTransactionId
    )

    this.currentCategories = []

    if (badgesContainer) {
      const badges = badgesContainer.querySelectorAll("span")
      badges.forEach((badge) => {
        const isAiBadge = badge.hasAttribute("data-ai-badge")
        const text = badge.textContent?.trim() || ""
        // Strip leading "?" from AI badge text
        const cleanText = text.replace(/\?\s*$/, "").trim()
        const match = cleanText.match(/^(.+?)(?:\s+\$(\d+\.?\d*?))?$/)
        if (match) {
          const name = match[1].trim()
          const amount = match[2] ? parseFloat(match[2]) * 100 : null
          const category = this.allCategories.find((c) => c.name === name)
          if (category) {
            this.currentCategories.push({
              id: category.id,
              name: category.name,
              amount_cents: amount,
              ai_predicted: isAiBadge && this.currentAiPrediction !== null,
              ai_confidence: isAiBadge && this.currentAiPrediction ? this.currentAiPrediction.confidence : null,
            })
          }
        }
      })
    }

    this.renderCurrentCategories()
  }

  renderCurrentCategories() {
    this.currentCategoriesTarget.innerHTML = ""

    if (this.currentCategories.length === 0) {
      this.noCategoriesTarget.classList.remove("hidden")
    } else {
      this.noCategoriesTarget.classList.add("hidden")

      this.currentCategories.forEach((cat) => {
        const row = document.createElement("div")
        if (cat.ai_predicted) {
          row.className = "flex items-center gap-3 p-3 bg-purple-50 rounded-lg border-l-4 border-purple-400"
        } else {
          row.className = "flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
        }
        row.dataset.categoryId = cat.id

        // Category name
        const name = document.createElement("span")
        name.className = cat.ai_predicted
          ? "flex-1 font-medium text-purple-900"
          : "flex-1 font-medium text-gray-900"
        name.textContent = cat.name
        if (cat.ai_predicted) {
          const aiLabel = document.createElement("span")
          aiLabel.className = "ml-2 text-xs text-purple-500 font-normal"
          aiLabel.textContent = "AI suggested"
          name.appendChild(aiLabel)
        }
        row.appendChild(name)

        // Amount input
        const amountInput = document.createElement("input")
        amountInput.type = "number"
        amountInput.step = "0.01"
        amountInput.min = "0"
        amountInput.placeholder = (this.currentTransactionAmount / 100).toFixed(2)
        amountInput.value = cat.amount_cents ? (cat.amount_cents / 100).toFixed(2) : ""
        amountInput.style.cssText =
          "width: 100px; padding: 6px 10px; font-size: 14px; border: 1px solid #d1d5db; border-radius: 6px; text-align: right;"
        amountInput.addEventListener("input", () => {
          this.updateRemainingAmountFromInputs()
        })
        amountInput.addEventListener("blur", (e) => {
          this.updateAmountForCategory(cat.id, (e.target as HTMLInputElement).value)
        })
        row.appendChild(amountInput)

        // Remove button (not shown for AI predictions — use confirm/reject instead)
        if (!cat.ai_predicted) {
          const removeBtn = document.createElement("button")
          removeBtn.type = "button"
          removeBtn.style.cssText = "color: #9ca3af; padding: 4px; display: flex; align-items: center;"
          removeBtn.title = "Remove"
          removeBtn.innerHTML = `<svg style="width: 18px; height: 18px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>`
          removeBtn.addEventListener("click", (e) => {
            e.stopPropagation()
            this.removeCategoryById(cat.id)
          })
          removeBtn.addEventListener("mouseenter", () => {
            removeBtn.style.color = "#ef4444"
          })
          removeBtn.addEventListener("mouseleave", () => {
            removeBtn.style.color = "#9ca3af"
          })
          row.appendChild(removeBtn)
        }

        this.currentCategoriesTarget.appendChild(row)
      })
    }

    this.updateRemainingAmount()
  }

  updateRemainingAmount() {
    const amounts = this.currentCategories
      .map((c) => c.amount_cents)
      .filter((a): a is number => a !== null && a !== undefined)

    if (amounts.length > 0) {
      const total = amounts.reduce((sum, a) => sum + a, 0)
      const remaining = this.currentTransactionAmount - total

      if (remaining > 0) {
        this.remainingAmountTarget.textContent = `$${(remaining / 100).toFixed(2)} remaining to categorize`
        this.remainingAmountTarget.classList.remove("hidden")
      } else if (remaining < 0) {
        this.remainingAmountTarget.textContent = `Over by $${(Math.abs(remaining) / 100).toFixed(2)}`
        this.remainingAmountTarget.classList.remove("hidden")
      } else {
        this.remainingAmountTarget.classList.add("hidden")
      }
    } else {
      this.remainingAmountTarget.classList.add("hidden")
    }
  }

  // Search functionality
  onSearchInput(event: Event) {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase()
    this.renderDropdown(query)
  }

  onSearchFocus() {
    const query = this.searchInputTarget.value.trim().toLowerCase()
    this.renderDropdown(query)
  }

  renderDropdown(query: string) {
    const assignedIds = this.currentCategories.map((c) => c.id)
    let matches = this.allCategories.filter((c) => !assignedIds.includes(c.id))

    if (query) {
      matches = matches.filter((c) => c.name.toLowerCase().includes(query))
    }

    this.dropdownListTarget.innerHTML = ""

    // Show matching categories
    matches.slice(0, 8).forEach((cat) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className =
        "w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
      item.dataset.action = "click->category-panel#selectCategory"
      item.dataset.categoryId = cat.id
      item.dataset.categoryName = cat.name
      item.textContent = cat.name
      this.dropdownListTarget.appendChild(item)
    })

    // Show "Create" option if query doesn't exactly match
    if (query && !this.allCategories.some((c) => c.name.toLowerCase() === query)) {
      const createItem = document.createElement("button")
      createItem.type = "button"
      createItem.className = "w-full px-4 py-3 text-left text-sm text-blue-600 hover:bg-blue-50 font-medium"
      createItem.dataset.action = "click->category-panel#createAndSelectCategory"
      createItem.dataset.categoryName = query
      createItem.innerHTML = `<span class="mr-2">+</span> Create "${this.searchInputTarget.value.trim()}"`
      this.dropdownListTarget.appendChild(createItem)
    }

    // Show/hide dropdown
    if (this.dropdownListTarget.children.length > 0) {
      this.dropdownTarget.classList.remove("hidden")
    } else {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden")
  }

  onSearchEnter(event: Event) {
    event.preventDefault()
    const firstItem = this.dropdownListTarget.querySelector("button")
    if (firstItem) {
      firstItem.click()
    }
  }

  async selectCategory(event: Event) {
    const target = event.currentTarget as HTMLElement
    const categoryId = target.dataset.categoryId!
    const categoryName = target.dataset.categoryName!

    await this.addCategoryToTransaction(categoryId, categoryName)
  }

  async createAndSelectCategory(event: Event) {
    const name = this.searchInputTarget.value.trim()
    if (!name) return

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    try {
      const response = await fetch(this.categoriesUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({ transaction_category: { name } }),
      })

      const data = await response.json()

      if (response.ok) {
        // Add to all categories
        this.allCategories.push({ id: data.id, name: data.name })
        // Add to transaction
        await this.addCategoryToTransaction(data.id, data.name)
      } else {
        this.showError(data.errors.join(", "))
      }
    } catch (error) {
      this.showError("Failed to create category")
    }
  }

  async addCategoryToTransaction(categoryId: string, categoryName: string) {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    try {
      const response = await fetch(this.currentUpdateUrl!, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({
          action_type: "add",
          category_id: categoryId,
          amount_cents: null,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        this.currentCategories.push({ id: categoryId, name: categoryName, amount_cents: null })
        this.renderCurrentCategories()
        this.updateTransactionBadges()
        this.searchInputTarget.value = ""
        this.hideDropdown()
        this.clearError()

        // Update training progress banner if present
        if (data.training_progress) {
          this.updateTrainingBanner(data.training_progress)
        }
      } else {
        this.showError(data.errors.join(", "))
      }
    } catch (error) {
      this.showError("Failed to add category")
    }
  }

  async removeCategory(event: Event) {
    event.stopPropagation()
    const categoryId = (event.currentTarget as HTMLElement).dataset.categoryId!
    await this.removeCategoryById(categoryId)
  }

  async removeCategoryById(categoryId: string) {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    try {
      const response = await fetch(this.currentUpdateUrl!, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({
          action_type: "remove",
          category_id: categoryId,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        this.currentCategories = this.currentCategories.filter((c) => c.id !== categoryId)
        this.renderCurrentCategories()
        this.updateTransactionBadges()
        this.clearError()
      } else {
        this.showError(data.errors.join(", "))
      }
    } catch (error) {
      this.showError("Failed to remove category")
    }
  }

  // Amount editing
  async updateAmountForCategory(categoryId: string, value: string) {
    const amountCents = value ? Math.round(parseFloat(value) * 100) : null

    // Update remaining amount display immediately based on all input values
    this.updateRemainingAmountFromInputs()

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    try {
      const response = await fetch(this.currentUpdateUrl!, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({
          action_type: "update_amount",
          category_id: categoryId,
          amount_cents: amountCents,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        // Only update local state after successful save
        const category = this.currentCategories.find((c) => c.id === categoryId)
        if (category) {
          category.amount_cents = amountCents
        }
        this.updateTransactionBadges()
        this.clearError()
      } else {
        this.showError(data.errors.join(", "))
      }
    } catch (error) {
      this.showError("Failed to update amount")
    }
  }

  updateRemainingAmountFromInputs() {
    // Get all amount inputs currently in the panel
    const inputs = this.currentCategoriesTarget.querySelectorAll<HTMLInputElement>('input[type="number"]')
    let total = 0

    inputs.forEach((input) => {
      if (input.value) {
        total += Math.round(parseFloat(input.value) * 100)
      }
    })

    const remaining = this.currentTransactionAmount - total

    if (total > 0) {
      if (remaining > 0) {
        this.remainingAmountTarget.textContent = `$${(remaining / 100).toFixed(2)} remaining`
        this.remainingAmountTarget.classList.remove("hidden")
      } else if (remaining < 0) {
        this.remainingAmountTarget.textContent = `Over by $${(Math.abs(remaining) / 100).toFixed(2)}`
        this.remainingAmountTarget.classList.remove("hidden")
      } else {
        this.remainingAmountTarget.textContent = `Fully allocated`
        this.remainingAmountTarget.classList.remove("hidden")
      }
    } else {
      this.remainingAmountTarget.classList.add("hidden")
    }
  }

  updateTransactionBadges() {
    const badgesContainer = this.badgesTargets.find(
      (b) => b.dataset.transactionId === this.currentTransactionId
    )

    if (!badgesContainer) return

    const existingBadges = badgesContainer.querySelectorAll("span")
    existingBadges.forEach((badge) => badge.remove())

    const button = badgesContainer.querySelector("button")
    this.currentCategories.forEach((cat) => {
      const amountDisplay = cat.amount_cents ? ` $${(cat.amount_cents / 100).toFixed(2)}` : ""
      const badge = document.createElement("span")
      badge.className =
        "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
      badge.textContent = `${cat.name}${amountDisplay}`
      button?.insertAdjacentElement("beforebegin", badge)
    })
  }

  async confirmAiPrediction() {
    if (!this.currentAiPrediction || !this.currentTransactionId) return

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    try {
      const response = await fetch(`/transactions/${this.currentTransactionId}/confirm_ai_prediction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()

        // Hide the AI suggestion banner
        this.aiSuggestionTarget.classList.add("hidden")

        // Update the category in currentCategories to no longer be AI-predicted
        const cat = this.currentCategories.find((c) => c.id === this.currentAiPrediction!.categoryId)
        if (cat) {
          cat.ai_predicted = false
          cat.ai_confidence = null
        }
        this.renderCurrentCategories()

        // Update the transaction row badges — replace purple with gray confirmed
        this.updateTransactionBadgesAfterConfirm()

        // Clear AI data attributes from the row
        const row = this.element.querySelector(`[data-transaction-id="${this.currentTransactionId}"]`) as HTMLElement
        if (row) {
          delete row.dataset.transactionAiNeedsReview
          delete row.dataset.transactionAiCategoryName
          delete row.dataset.transactionAiCategoryId
          delete row.dataset.transactionAiConfidence
        }

        // Update training progress banner if returned
        if (data.training_progress) {
          this.updateTrainingBanner(data.training_progress)
        }

        // Decrement review counter
        this.updateReviewCounter(-1)

        this.currentAiPrediction = null
        this.clearError()
      } else {
        const data = await response.json()
        this.showError(data.error || "Failed to confirm prediction")
      }
    } catch (error) {
      this.showError("Failed to confirm prediction")
    }
  }

  async rejectAiPrediction() {
    if (!this.currentAiPrediction || !this.currentTransactionId) return

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    try {
      const response = await fetch(`/transactions/${this.currentTransactionId}/reject_ai_prediction`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
      })

      if (response.ok) {
        // Hide the AI suggestion banner
        this.aiSuggestionTarget.classList.add("hidden")

        // Remove the AI category from currentCategories
        this.currentCategories = this.currentCategories.filter(
          (c) => c.id !== this.currentAiPrediction!.categoryId
        )
        this.renderCurrentCategories()

        // Update the transaction row badges
        this.updateTransactionBadges()

        // Clear AI data attributes from the row
        const row = this.element.querySelector(`[data-transaction-id="${this.currentTransactionId}"]`) as HTMLElement
        if (row) {
          delete row.dataset.transactionAiNeedsReview
          delete row.dataset.transactionAiCategoryName
          delete row.dataset.transactionAiCategoryId
          delete row.dataset.transactionAiConfidence
        }

        // Decrement review counter
        this.updateReviewCounter(-1)

        this.currentAiPrediction = null
        this.clearError()
      } else {
        const data = await response.json()
        this.showError(data.error || "Failed to reject prediction")
      }
    } catch (error) {
      this.showError("Failed to reject prediction")
    }
  }

  private updateTransactionBadgesAfterConfirm() {
    const badgesContainer = this.badgesTargets.find(
      (b) => b.dataset.transactionId === this.currentTransactionId
    )
    if (!badgesContainer) return

    // Find and restyle AI badges to confirmed (gray)
    const aiBadges = badgesContainer.querySelectorAll("[data-ai-badge]")
    aiBadges.forEach((badge) => {
      badge.classList.remove("bg-purple-100", "text-purple-700")
      badge.classList.add("bg-gray-100", "text-gray-700")
      // Remove the "?" indicator and AI icon
      const questionMark = badge.querySelector(".text-purple-500")
      if (questionMark) questionMark.remove()
      const aiIcon = badge.querySelector("svg")
      if (aiIcon) aiIcon.remove()
      badge.removeAttribute("data-ai-badge")
    })
  }

  private updateReviewCounter(delta: number) {
    const counter = document.querySelector('[data-ai-review-target="counter"]')
    if (!counter) return

    const current = parseInt(counter.textContent || "0") || 0
    const newCount = Math.max(0, current + delta)
    counter.textContent = String(newCount)

    if (newCount === 0) {
      const badge = counter.closest("[data-review-badge]")
      if (badge) badge.classList.add("hidden")
    }
  }

  showError(message: string) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorMessageTarget.classList.add("hidden")
  }

  // Close dropdown when clicking outside
  closeDropdownOnClickOutside(event: Event) {
    if (
      !this.dropdownTarget.contains(event.target as Node) &&
      !this.searchInputTarget.contains(event.target as Node)
    ) {
      this.hideDropdown()
    }
  }

  // System category toggle methods
  updateSystemToggleUI() {
    this.updateToggleButton(this.internalToggleTarget, this.currentInternal, "bg-blue-600")
    this.updateToggleButton(this.incomeToggleTarget, this.currentIncome, "bg-green-600")
    this.updateCategorySectionVisibility()
  }

  updateToggleButton(toggle: HTMLElement | null, isActive: boolean, activeColor: string) {
    if (!toggle) return

    const span = toggle.querySelector("span")
    if (!span) return

    if (isActive) {
      toggle.classList.remove("bg-gray-200")
      toggle.classList.add(activeColor)
      toggle.setAttribute("aria-checked", "true")
      span.classList.remove("translate-x-0")
      span.classList.add("translate-x-5")
    } else {
      toggle.classList.remove(activeColor)
      toggle.classList.add("bg-gray-200")
      toggle.setAttribute("aria-checked", "false")
      span.classList.remove("translate-x-5")
      span.classList.add("translate-x-0")
    }
  }

  updateCategorySectionVisibility() {
    if (!this.hasCategorySectionTarget) return

    const hasSystemCategory = this.currentInternal || this.currentIncome

    if (hasSystemCategory) {
      this.categorySectionTarget.classList.add("opacity-40", "pointer-events-none")
      this.searchInputTarget.disabled = true
    } else {
      this.categorySectionTarget.classList.remove("opacity-40", "pointer-events-none")
      this.searchInputTarget.disabled = false
    }
  }

  async toggleInternal() {
    await this.toggleSystemCategory("toggle_internal", "internal", !this.currentInternal)
  }

  async toggleIncome() {
    await this.toggleSystemCategory("toggle_income", "income", !this.currentIncome)
  }

  async toggleSystemCategory(actionType: string, paramName: string, newValue: boolean) {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!.content

    try {
      const response = await fetch(this.currentUpdateUrl!, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: JSON.stringify({
          action_type: actionType,
          [paramName]: newValue,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        // Update both states from response (they're mutually exclusive)
        this.currentInternal = data.internal
        this.currentIncome = data.income
        this.updateSystemToggleUI()
        this.updateSystemBadges()

        // When enabling any system category, clear all regular categories from UI
        if (this.currentInternal || this.currentIncome) {
          this.currentCategories = []
          this.renderCurrentCategories()
          this.updateTransactionBadges()
        }

        // Update training progress banner if present
        if (data.training_progress) {
          this.updateTrainingBanner(data.training_progress)
        }

        this.clearError()
      } else {
        this.showError(data.errors.join(", "))
      }
    } catch (error) {
      this.showError("Failed to update status")
    }
  }

  updateSystemBadges() {
    const row = this.element.querySelector(`[data-transaction-id="${this.currentTransactionId}"]`)
    if (!row) return

    ;(row as HTMLElement).dataset.transactionInternal = this.currentInternal.toString()
    ;(row as HTMLElement).dataset.transactionIncome = this.currentIncome.toString()

    const nameContainer = row.querySelector(".font-medium.text-gray-900")
    if (!nameContainer) return

    // Update Internal badge
    let internalBadge = nameContainer.querySelector(".internal-badge")
    if (this.currentInternal && !internalBadge) {
      internalBadge = document.createElement("span")
      internalBadge.className =
        "internal-badge ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
      internalBadge.textContent = "Internal"
      nameContainer.appendChild(internalBadge)
    } else if (!this.currentInternal && internalBadge) {
      internalBadge.remove()
    }

    // Update Income badge
    let incomeBadge = nameContainer.querySelector(".income-badge")
    if (this.currentIncome && !incomeBadge) {
      incomeBadge = document.createElement("span")
      incomeBadge.className =
        "income-badge ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700"
      incomeBadge.textContent = "Income"
      nameContainer.appendChild(incomeBadge)
    } else if (!this.currentIncome && incomeBadge) {
      incomeBadge.remove()
    }
  }

  updateTrainingBanner(progress: { current: number; required: number; complete?: boolean }) {
    // Update progress text (e.g., "12/30")
    const progressText = document.querySelector('[data-ai-review-target="trainingProgress"]')
    if (progressText) {
      progressText.textContent = `${progress.current}/${progress.required}`
    }

    // Update progress bar width
    const progressBar = document.querySelector<HTMLElement>('[data-ai-review-target="trainingBar"]')
    if (progressBar) {
      const percentage = Math.min(100, (progress.current / progress.required) * 100)
      progressBar.style.width = `${percentage}%`
    }

    // Hide banner if training is complete
    if (progress.complete) {
      const banner = progressText?.closest(".bg-gradient-to-r") as HTMLElement | null
      if (banner) {
        banner.style.transition = "opacity 0.5s ease-out"
        banner.style.opacity = "0"
        setTimeout(() => banner.remove(), 500)
      }
    }
  }
}
